export interface OpenAIModelPricing {
  provider: "openai";
  model: string;
  currency: "USD";
  inputUsdPerMillion: number;
  cachedInputUsdPerMillion: number;
  cacheWriteUsdPerMillion?: number | null;
  outputUsdPerMillion: number;
  contextTier?: "short" | "long";
  longContextThresholdTokens?: number | null;
  longContextPricing?: {
    inputUsdPerMillion: number;
    cachedInputUsdPerMillion: number;
    cacheWriteUsdPerMillion?: number | null;
    outputUsdPerMillion: number;
  };
  sourceUrl: string;
  effectiveDate: string;
}

export interface NormalizedOpenAIUsage {
  inputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  uncachedInputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
}

export interface OpenAITokenSpend {
  provider: "openai";
  model: string;
  inputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  uncachedInputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  amountMicros: number;
  currency: "USD";
  inputPricePerMillionMicros: number;
  cachedInputPricePerMillionMicros: number;
  cacheWritePricePerMillionMicros: number | null;
  cacheWriteCostMicros: number;
  outputPricePerMillionMicros: number;
  pricingContextTier: "short" | "long";
  longContextThresholdTokens: number | null;
  pricingSource: string;
  pricingEffectiveAt: string;
  usage: NormalizedOpenAIUsage;
}

const OPENAI_GPT_5_4_PRICING_SOURCE_URL =
  "https://developers.openai.com/api/docs/models/gpt-5.4/";
const OPENAI_GPT_5_6_PRICING_SOURCE_URL =
  "https://developers.openai.com/api/docs/pricing";
const OPENAI_LONG_CONTEXT_THRESHOLD_TOKENS = 272_000;

export const OPENAI_MODEL_PRICING_USD_PER_MILLION: Record<
  string,
  OpenAIModelPricing
> = {
  "gpt-5.4": {
    provider: "openai",
    model: "gpt-5.4",
    currency: "USD",
    inputUsdPerMillion: 2.5,
    cachedInputUsdPerMillion: 0.25,
    outputUsdPerMillion: 15,
    sourceUrl: OPENAI_GPT_5_4_PRICING_SOURCE_URL,
    effectiveDate: "2026-05-19",
  },
  "gpt-5.6-luna": {
    provider: "openai",
    model: "gpt-5.6-luna",
    currency: "USD",
    inputUsdPerMillion: 0.2,
    cachedInputUsdPerMillion: 0.02,
    cacheWriteUsdPerMillion: 0.25,
    outputUsdPerMillion: 1.2,
    sourceUrl: OPENAI_GPT_5_6_PRICING_SOURCE_URL,
    effectiveDate: "2026-07-30",
    longContextThresholdTokens: OPENAI_LONG_CONTEXT_THRESHOLD_TOKENS,
    longContextPricing: {
      inputUsdPerMillion: 0.4,
      cachedInputUsdPerMillion: 0.04,
      cacheWriteUsdPerMillion: 0.5,
      outputUsdPerMillion: 1.8,
    },
  },
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;
}

function numberField(record: Record<string, unknown> | null, key: string) {
  const value = record?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function microsPerMillion(usdPerMillion: number): number {
  return Math.round(usdPerMillion * 1_000_000);
}

export function getOpenAIModelPricing(
  model: string,
  inputTokens = 0,
): OpenAIModelPricing | null {
  const pricing = OPENAI_MODEL_PRICING_USD_PER_MILLION[model];
  if (!pricing) return null;
  const threshold = pricing.longContextThresholdTokens ?? null;
  if (
    pricing.longContextPricing &&
    typeof threshold === "number" &&
    inputTokens > threshold
  ) {
    return {
      ...pricing,
      ...pricing.longContextPricing,
      contextTier: "long",
    };
  }
  return { ...pricing, contextTier: "short" };
}

export function normalizeOpenAIUsage(rawUsage: unknown): NormalizedOpenAIUsage {
  const usage = asRecord(rawUsage);
  if (!usage) {
    return {
      inputTokens: 0,
      cachedInputTokens: 0,
      cacheWriteTokens: 0,
      uncachedInputTokens: 0,
      outputTokens: 0,
      reasoningTokens: 0,
      totalTokens: 0,
    };
  }

  const inputTokens =
    numberField(usage, "prompt_tokens") || numberField(usage, "input_tokens");
  const outputTokens =
    numberField(usage, "completion_tokens") ||
    numberField(usage, "output_tokens");
  const totalTokens =
    numberField(usage, "total_tokens") || inputTokens + outputTokens;
  const inputDetails =
    asRecord(usage.prompt_tokens_details) ||
    asRecord(usage.input_tokens_details);
  const outputDetails =
    asRecord(usage.completion_tokens_details) ||
    asRecord(usage.output_tokens_details);
  const cachedInputTokens = Math.min(
    inputTokens,
    numberField(inputDetails, "cached_tokens") ||
      numberField(inputDetails, "cached_input_tokens"),
  );
  const cacheWriteTokens = Math.min(
    Math.max(inputTokens - cachedInputTokens, 0),
    numberField(inputDetails, "cache_write_tokens"),
  );
  const reasoningTokens =
    numberField(outputDetails, "reasoning_tokens") ||
    numberField(outputDetails, "reasoning_output_tokens");

  return {
    inputTokens,
    cachedInputTokens,
    cacheWriteTokens,
    uncachedInputTokens: Math.max(
      inputTokens - cachedInputTokens - cacheWriteTokens,
      0,
    ),
    outputTokens,
    reasoningTokens,
    totalTokens,
  };
}

export function calculateOpenAITokenSpend(
  model: string,
  rawUsage: unknown,
): OpenAITokenSpend | null {
  const usage = normalizeOpenAIUsage(rawUsage);
  const pricing = getOpenAIModelPricing(model, usage.inputTokens);
  if (!pricing) return null;
  const inputPricePerMillionMicros = microsPerMillion(
    pricing.inputUsdPerMillion,
  );
  const cachedInputPricePerMillionMicros = microsPerMillion(
    pricing.cachedInputUsdPerMillion,
  );
  const outputPricePerMillionMicros = microsPerMillion(
    pricing.outputUsdPerMillion,
  );
  const cacheWritePricePerMillionMicros =
    typeof pricing.cacheWriteUsdPerMillion === "number"
      ? microsPerMillion(pricing.cacheWriteUsdPerMillion)
      : null;
  const cacheWriteCostMicros = Math.round(
    (usage.cacheWriteTokens *
      (cacheWritePricePerMillionMicros ?? inputPricePerMillionMicros)) /
      1_000_000,
  );
  const amountMicros = Math.round(
    (usage.uncachedInputTokens * inputPricePerMillionMicros +
      usage.cachedInputTokens * cachedInputPricePerMillionMicros +
      usage.cacheWriteTokens *
        (cacheWritePricePerMillionMicros ?? inputPricePerMillionMicros) +
      usage.outputTokens * outputPricePerMillionMicros) /
      1_000_000,
  );

  return {
    provider: "openai",
    model,
    inputTokens: usage.inputTokens,
    cachedInputTokens: usage.cachedInputTokens,
    cacheWriteTokens: usage.cacheWriteTokens,
    uncachedInputTokens: usage.uncachedInputTokens,
    outputTokens: usage.outputTokens,
    reasoningTokens: usage.reasoningTokens,
    totalTokens: usage.totalTokens,
    amountMicros,
    currency: "USD",
    inputPricePerMillionMicros,
    cachedInputPricePerMillionMicros,
    cacheWritePricePerMillionMicros,
    cacheWriteCostMicros,
    outputPricePerMillionMicros,
    pricingContextTier: pricing.contextTier || "short",
    longContextThresholdTokens: pricing.longContextThresholdTokens ?? null,
    pricingSource: pricing.sourceUrl,
    pricingEffectiveAt: pricing.effectiveDate,
    usage,
  };
}
